import { useCallback, useEffect, useMemo, useState } from "react";

import {
  isDocxFile,
  isHtmlFile,
  isXlsxFile,
  isImageFile,
  isPdfFile,
  isSourceFile,
} from "@/lib/file-links";
import { useResizableWidth } from "@/hooks/use-resizable-width";

export type AsidePanelKind =
  | "agent"
  | "markdown"
  | "html"
  | "pdf"
  | "docx"
  | "image"
  | "source"
  | "thread"
  | "sub-agent"
  | "schedule-run"
  | "workflow-run"
  | "kanban-split";

export type AsidePanelResize = ReturnType<typeof useResizableWidth>;

export type AsideView =
  | { kind: "agent"; viewId: string }
  | { kind: "markdown"; file: AsideViewerFile }
  | { kind: "html"; file: AsideViewerFile }
  | { kind: "pdf"; file: AsideViewerFile }
  | { kind: "docx"; file: AsideViewerFile }
  | { kind: "image"; file: AsideViewerFile }
  | { kind: "source"; file: AsideViewerFile }
  | { kind: "thread"; sessionId: string }
  | { kind: "sub-agent"; sessionId: string }
  | { kind: "schedule-run"; sessionId: string }
  | { kind: "workflow-run"; taskId: string }
  | null;

export interface AsideViewerFile {
  url: string;
  fileName: string;
}

interface UseAsideViewManagerOptions {
  pathname: string;
  search: string;
  navigate: (to: string, options?: { replace?: boolean }) => void;
}

type AsideUrlTarget =
  | { kind: "agent"; viewId: string }
  | { kind: "file"; file: AsideViewerFile }
  | { kind: "thread"; sessionId: string }
  | { kind: "sub-agent"; sessionId: string }
  | { kind: "schedule-run"; sessionId: string }
  | { kind: "workflow-run"; taskId: string };

type PanelParseResult =
  | { state: "absent" }
  | { state: "invalid" }
  | { state: "valid"; target: AsideUrlTarget };

const ASIDE_QUERY_KEYS = [
  "panel",
  "panel_stack",
  "thread",
  "subagent",
  "schedule-run",
  "viewer",
  "name",
  "view",
  "popout",
] as const;

export const ASIDE_RESIZE_OPTIONS = {
  agent: {
    initial: 640,
    min: 420,
    max: 960,
    minPeer: 400,
    storageKey: "agent-view-width",
  },
  markdown: {
    initial: 480,
    min: 320,
    max: 800,
    minPeer: 400,
    storageKey: "md-viewer-width",
  },
  html: {
    initial: 480,
    min: 320,
    max: 800,
    minPeer: 400,
    storageKey: "html-viewer-width",
  },
  pdf: {
    initial: 640,
    min: 420,
    max: 960,
    minPeer: 400,
    storageKey: "pdf-viewer-width",
  },
  docx: {
    initial: 640,
    min: 420,
    max: 960,
    minPeer: 400,
    storageKey: "docx-viewer-width",
  },
  image: {
    initial: 640,
    min: 320,
    max: 960,
    minPeer: 400,
    storageKey: "image-viewer-width",
  },
  source: {
    initial: 640,
    min: 420,
    max: 960,
    minPeer: 400,
    storageKey: "source-viewer-width",
  },
  thread: {
    initial: 480,
    min: 320,
    max: 800,
    minPeer: 400,
    storageKey: "child-session-width",
  },
  "sub-agent": {
    initial: 480,
    min: 320,
    max: 800,
    minPeer: 400,
    storageKey: "sub-agent-session-width",
  },
  "schedule-run": {
    initial: 480, min: 320, max: 800, minPeer: 400,
    storageKey: "schedule-run-session-width",
  },
  "workflow-run": {
    initial: 640, min: 420, max: 960, minPeer: 400,
    storageKey: "workflow-run-width",
  },
  "kanban-split": {
    initial: 480,
    min: 320,
    max: 800,
    minPeer: 400,
    storageKey: "kanban-split-width",
    handleSide: "right",
  },
} as const;

function clearAsideQuery(params: URLSearchParams): void {
  for (const key of ASIDE_QUERY_KEYS) params.delete(key);
}

function queryUrl(pathname: string, params: URLSearchParams): string {
  const nextSearch = params.toString();
  const fragment = typeof window !== "undefined" && window.location.pathname === pathname
    ? window.location.hash
    : "";
  return `${nextSearch ? `${pathname}?${nextSearch}` : pathname}${fragment}`;
}

/**
 * The panel parameter is the canonical, single-panel URL contract:
 *
 * - `agent:<viewId>` / `conversation:<viewId>` for configured or pinned views
 * - `thread:<sessionId>` / `sub-agent:<sessionId>` / `schedule-run:<sessionId>`
 *   for child sessions and scheduled-run transcripts
 * - `file:<signed-url>` plus the existing `name` companion for viewers
 *
 * The file name remains separate because the existing signed-link flow needs
 * it to choose the correct viewer for URLs whose filename is opaque.
 */
function parsePanelParam(
  panel: string | null,
  fileName: string,
): PanelParseResult {
  if (panel === null) return { state: "absent" };
  const separator = panel.indexOf(":");
  if (separator <= 0 || separator === panel.length - 1) return { state: "invalid" };

  const kind = panel.slice(0, separator);
  const value = panel.slice(separator + 1);
  if (!value.trim()) return { state: "invalid" };

  if (kind === "agent" || kind === "conversation") {
    return {
      state: "valid",
      target: { kind: "agent", viewId: `${kind}:${value}` },
    };
  }
  if (kind === "thread") {
    return { state: "valid", target: { kind: "thread", sessionId: value } };
  }
  if (kind === "sub-agent" || kind === "subagent") {
    return { state: "valid", target: { kind: "sub-agent", sessionId: value } };
  }
  if (kind === "schedule-run") {
    return { state: "valid", target: { kind: "schedule-run", sessionId: value } };
  }
  if (kind === "workflow-run" && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
    return { state: "valid", target: { kind: "workflow-run", taskId: value } };
  }
  if (kind === "file") {
    return {
      state: "valid",
      target: { kind: "file", file: { url: value, fileName } },
    };
  }
  return { state: "invalid" };
}

function panelParamFor(target: AsideUrlTarget): string {
  switch (target.kind) {
    case "agent":
      // View keys are already scope-qualified as `agent:<id>` or
      // `conversation:<id>`, which also makes the URL readable.
      return legacyAgentViewId(target.viewId);
    case "file":
      return `file:${target.file.url}`;
    case "thread":
      return `thread:${target.sessionId}`;
    case "sub-agent":
      return `sub-agent:${target.sessionId}`;
    case "schedule-run":
      return `schedule-run:${target.sessionId}`;
    case "workflow-run":
      return `workflow-run:${target.taskId}`;
  }
}

function applyPanelTarget(
  params: URLSearchParams,
  target: AsideUrlTarget,
  isPopout = false,
): void {
  clearAsideQuery(params);
  params.set("panel", panelParamFor(target));
  if (target.kind === "file") {
    params.set("name", target.file.fileName);
    if (isPopout) params.set("popout", "1");
  }
}

function hasCanonicalPanelParams(
  params: URLSearchParams,
  target: AsideUrlTarget,
  isPopout: boolean,
): boolean {
  if (params.get("panel") !== panelParamFor(target)) return false;
  if (
    params.has("thread")
    || params.has("subagent")
    || params.has("schedule-run")
    || params.has("viewer")
    || params.has("view")
  ) {
    return false;
  }
  if (target.kind === "file") {
    return params.get("name") === target.file.fileName
      && (isPopout ? params.get("popout") === "1" : !params.has("popout"));
  }
  return !params.has("name") && !params.has("popout");
}

function legacyAgentViewId(viewId: string): string {
  return viewId.startsWith("agent:") || viewId.startsWith("conversation:")
    ? viewId
    : `agent:${viewId}`;
}

const MAX_STACK_ENTRIES = 4;
const MAX_STACK_CHARS = 2048;

function readPanelStack(raw: string | null): AsideUrlTarget[] {
  if (!raw || raw.length > MAX_STACK_CHARS) return [];
  try {
    const values: unknown = JSON.parse(raw);
    if (!Array.isArray(values) || values.length > MAX_STACK_ENTRIES) return [];
    const targets = values.map((value) =>
      typeof value === "string" ? parsePanelParam(value, "document.md") : { state: "invalid" as const });
    if (targets.some((target) => target.state !== "valid" || target.target.kind === "file")) return [];
    return targets.map((target) => (target as { state: "valid"; target: AsideUrlTarget }).target);
  } catch {
    return [];
  }
}

function writePanelStack(params: URLSearchParams, targets: AsideUrlTarget[]): void {
  if (targets.length) params.set("panel_stack", JSON.stringify(targets.map(panelParamFor)));
  else params.delete("panel_stack");
}

/**
 * Owns the mutually-exclusive in-chat aside and its canonical `panel` URL
 * parameter. Legacy viewer/thread params are read once and normalised into
 * that single parameter so rolling UI deployments retain old deep links.
 */
export function useAsideViewManager({
  pathname,
  search,
  navigate,
}: UseAsideViewManagerOptions) {
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const [scheduleRunOriginViewId, setScheduleRunOriginViewId] = useState<string | null>(null);
  const queryParams = useMemo(() => new URLSearchParams(search), [search]);
  const viewerName = queryParams.get("name") ?? "document.md";
  const parsedPanel = useMemo(
    () => parsePanelParam(queryParams.get("panel"), viewerName),
    [queryParams, viewerName],
  );
  const legacyViewerUrl = queryParams.get("viewer");
  const legacyThreadSessionId = queryParams.get("thread");
  const legacySubAgentSessionId = queryParams.get("subagent");
  const legacyScheduleRunSessionId = queryParams.get("schedule-run");
  const legacyViewId = queryParams.get("view");
  const isPopout = queryParams.get("popout") === "1";
  const legacyTarget = useMemo<AsideUrlTarget | null>(() => {
    // Keep the established viewer > schedule-run > sub-agent > thread > view
    // precedence while old links are still in circulation.
    if (legacyViewerUrl) {
      return { kind: "file", file: { url: legacyViewerUrl, fileName: viewerName } };
    }
    if (legacyScheduleRunSessionId) {
      return { kind: "schedule-run", sessionId: legacyScheduleRunSessionId };
    }
    if (legacySubAgentSessionId) {
      return { kind: "sub-agent", sessionId: legacySubAgentSessionId };
    }
    if (legacyThreadSessionId) {
      return { kind: "thread", sessionId: legacyThreadSessionId };
    }
    if (legacyViewId) {
      return { kind: "agent", viewId: legacyAgentViewId(legacyViewId) };
    }
    return null;
  }, [
    legacyScheduleRunSessionId,
    legacySubAgentSessionId,
    legacyThreadSessionId,
    legacyViewId,
    legacyViewerUrl,
    viewerName,
  ]);
  const urlTarget = parsedPanel.state === "valid"
    ? parsedPanel.target
    : parsedPanel.state === "absent"
      ? legacyTarget
      : null;
  const panelStack = useMemo(() => parsedPanel.state === "valid"
    ? readPanelStack(queryParams.get("panel_stack")) : [], [parsedPanel.state, queryParams]);
  const viewerFile = urlTarget?.kind === "file" ? urlTarget.file : null;
  const threadSessionId = urlTarget?.kind === "thread" ? urlTarget.sessionId : null;
  const subAgentSessionId = urlTarget?.kind === "sub-agent" ? urlTarget.sessionId : null;
  const scheduleRunSessionId = urlTarget?.kind === "schedule-run" ? urlTarget.sessionId : null;
  const workflowRunTaskId = urlTarget?.kind === "workflow-run" ? urlTarget.taskId : null;
  const urlActiveViewId = urlTarget?.kind === "agent" ? urlTarget.viewId : null;
  const viewerKind = viewerFile && (isHtmlFile(viewerFile.url, viewerFile.fileName) || isXlsxFile(viewerFile.url, viewerFile.fileName))
    ? "html"
    : viewerFile && isPdfFile(viewerFile.url, viewerFile.fileName)
      ? "pdf"
      : viewerFile && isDocxFile(viewerFile.url, viewerFile.fileName)
        ? "docx"
        : viewerFile && isSourceFile(viewerFile.url, viewerFile.fileName)
          ? "source"
          : viewerFile && isImageFile(viewerFile.url, viewerFile.fileName)
            ? "image"
            : "markdown";

  // URL-backed panels win over an in-memory agent selection. This makes a
  // pasted/back-forward URL safe even before its state has been normalised.
  const aside = useMemo<AsideView>(() => {
    if (parsedPanel.state === "invalid") return null;
    if (viewerFile && !isPopout) return { kind: viewerKind, file: viewerFile };
    if (scheduleRunSessionId) return { kind: "schedule-run", sessionId: scheduleRunSessionId };
    if (subAgentSessionId) return { kind: "sub-agent", sessionId: subAgentSessionId };
    if (threadSessionId) return { kind: "thread", sessionId: threadSessionId };
    if (workflowRunTaskId) return { kind: "workflow-run", taskId: workflowRunTaskId };
    if (urlActiveViewId ?? activeViewId) {
      return { kind: "agent", viewId: urlActiveViewId ?? activeViewId! };
    }
    return null;
  }, [
    activeViewId,
    isPopout,
    parsedPanel.state,
    scheduleRunSessionId,
    subAgentSessionId,
    threadSessionId,
    workflowRunTaskId,
    urlActiveViewId,
    viewerFile,
    viewerKind,
  ]);

  const navigateWithParams = useCallback((
    update: (params: URLSearchParams) => void,
    targetPathname = pathname,
  ) => {
    const params = new URLSearchParams(search);
    const before = params.toString();
    update(params);
    const next = params.toString();
    if (next === before && targetPathname === pathname) return;
    navigate(queryUrl(targetPathname, params), { replace: true });
  }, [navigate, pathname, search]);

  const openAgentView = useCallback((viewId: string) => {
    const scopedViewId = legacyAgentViewId(viewId);
    setActiveViewId(scopedViewId);
    setScheduleRunOriginViewId(null);
    navigateWithParams((params) => {
      applyPanelTarget(params, { kind: "agent", viewId: scopedViewId });
    });
  }, [navigateWithParams]);

  const openViewer = useCallback((url: string, fileName: string) => {
    setActiveViewId(null);
    setScheduleRunOriginViewId(null);
    navigateWithParams((params) => {
      applyPanelTarget(params, { kind: "file", file: { url, fileName } });
    });
  }, [navigateWithParams]);

  const openThread = useCallback((childSessionId: string, targetPathname?: string) => {
    setActiveViewId(null);
    setScheduleRunOriginViewId(null);
    navigateWithParams((params) => {
      applyPanelTarget(params, { kind: "thread", sessionId: childSessionId });
    }, targetPathname);
  }, [navigateWithParams]);

  const openWorkflowRun = useCallback((taskId: string) => {
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(taskId)) return;
    setActiveViewId(null);
    navigateWithParams((params) => applyPanelTarget(params, { kind: "workflow-run", taskId }));
  }, [navigateWithParams]);

  const pushThread = useCallback((sessionId: string) => {
    if (!urlTarget || urlTarget.kind !== "workflow-run"
      || panelStack.length >= MAX_STACK_ENTRIES
      || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(sessionId)) return;
    navigateWithParams((params) => {
      applyPanelTarget(params, { kind: "thread", sessionId });
      writePanelStack(params, [...panelStack, urlTarget]);
    });
  }, [navigateWithParams, panelStack, urlTarget]);

  const popAside = useCallback(() => {
    if (!panelStack.length) return;
    const top = panelStack[panelStack.length - 1];
    navigateWithParams((params) => {
      applyPanelTarget(params, top);
      writePanelStack(params, panelStack.slice(0, -1));
    });
  }, [navigateWithParams, panelStack]);

  const openSubAgent = useCallback((childSessionId: string, targetPathname?: string) => {
    setActiveViewId(null);
    setScheduleRunOriginViewId(null);
    navigateWithParams((params) => {
      applyPanelTarget(params, { kind: "sub-agent", sessionId: childSessionId });
    }, targetPathname);
  }, [navigateWithParams]);

  const openScheduleRun = useCallback((sessionId: string) => {
    // A selected schedule-history view may already be represented by the
    // canonical agent target. Keep that origin while the transcript replaces
    // the panel, so closing returns to the same history tab.
    setScheduleRunOriginViewId((currentOrigin) => (
      urlActiveViewId ?? activeViewId ?? currentOrigin
    ));
    navigateWithParams((params) => {
      applyPanelTarget(params, { kind: "schedule-run", sessionId });
    });
  }, [activeViewId, navigateWithParams, urlActiveViewId]);

  const closeAside = useCallback(() => {
    setActiveViewId(null);
    setScheduleRunOriginViewId(null);
    navigateWithParams(clearAsideQuery);
  }, [navigateWithParams]);

  const closeScheduleRun = useCallback(() => {
    if (scheduleRunOriginViewId) setActiveViewId(scheduleRunOriginViewId);
    setScheduleRunOriginViewId(null);
    navigateWithParams(clearAsideQuery);
  }, [navigateWithParams, scheduleRunOriginViewId]);

  const buildViewerPopoutUrl = useCallback((
    url: string,
    fileName: string,
    targetPathname = pathname,
  ) => {
    const params = new URLSearchParams(search);
    applyPanelTarget(params, { kind: "file", file: { url, fileName } }, true);
    return queryUrl(targetPathname, params);
  }, [pathname, search]);

  // A malformed panel target cannot render safely. Valid legacy URLs are
  // normalised into the canonical parameter with replace navigation, keeping
  // browser history clean while allowing independently deployed UI versions.
  useEffect(() => {
    if (parsedPanel.state === "invalid") {
      navigateWithParams(clearAsideQuery);
      return;
    }
    if (urlTarget && (!hasCanonicalPanelParams(queryParams, urlTarget, isPopout)
      || (queryParams.has("panel_stack") && !panelStack.length))) {
      navigateWithParams((params) => {
        applyPanelTarget(params, urlTarget, isPopout);
        writePanelStack(params, panelStack);
      });
    }
  }, [isPopout, navigateWithParams, panelStack, parsedPanel.state, queryParams, urlTarget]);

  // In-memory agent selection only bridges the render before navigation
  // publishes its URL. Once any URL target is present (or malformed), retire
  // that handoff so manually removing `panel` cannot resurrect an old aside.
  useEffect(() => {
    if (urlTarget || parsedPanel.state === "invalid") setActiveViewId(null);
    if (urlTarget?.kind !== "schedule-run") setScheduleRunOriginViewId(null);
  }, [parsedPanel.state, urlTarget]);

  const selectedViewId = urlActiveViewId
    ?? (urlTarget?.kind === "schedule-run" ? scheduleRunOriginViewId : activeViewId);
  const resize = useAsidePanelResize(aside?.kind ?? "markdown");

  return {
    aside,
    activeViewId: selectedViewId,
    viewerFile,
    viewerIsHtml: viewerKind === "html",
    viewerIsPdf: viewerKind === "pdf",
    viewerIsDocx: viewerKind === "docx",
    viewerIsImage: viewerKind === "image",
    viewerIsSource: viewerKind === "source",
    isPopout,
    threadSessionId,
    subAgentSessionId,
    scheduleRunSessionId,
    workflowRunTaskId,
    stackDepth: panelStack.length,
    resize,
    openAgentView,
    openViewer,
    openThread,
    openWorkflowRun,
    pushThread,
    popAside,
    openSubAgent,
    openScheduleRun,
    closeAside,
    closeAgentView: closeAside,
    closeViewer: closeAside,
    closeThread: closeAside,
    closeSubAgent: closeAside,
    closeScheduleRun,
    buildViewerPopoutUrl,
  };
}

/**
 * Shared width/persistence policy for every desktop aside panel. The active
 * kind selects its existing localStorage key so users retain their familiar
 * per-panel widths while only one resize controller is mounted at a time.
 */
export function useAsidePanelResize(kind: AsidePanelKind): AsidePanelResize {
  return useResizableWidth(ASIDE_RESIZE_OPTIONS[kind]);
}
